import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import Preloader from '@/components/Preloader';
import { reais, type DadosDoRelatorio as Dados } from './tipos';

/** Contagem e percentual não são dinheiro. */
const NAO_MONETARIOS = ['funcionarios', 'aliquota_inss_autonomos'];

function formatar(campo: string, valor: number): string {
  return NAO_MONETARIOS.includes(campo) ? valor.toLocaleString('pt-BR') : reais.format(valor);
}

/**
 * O que o escritório mandou no relatório de um setor (ou no totalizador), campo
 * a campo, ao lado do que o PDF da folha trazia — a diferença é o que o
 * escritório atualizou depois.
 */
export default function DadosDoRelatorio({ folhaId, origem }: { folhaId: string; origem: number | 'totalizador' }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['folha', folhaId, 'relatorio', origem],
    queryFn: async () => (await api.get<{ data: Dados }>(`/folhas/${folhaId}/relatorios/${origem}`)).data.data,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-sm py-sm text-[13px] text-muted">
        <Preloader />
        Carregando os dados do relatório…
      </div>
    );
  }

  if (isError || !data) {
    return <p className="py-sm text-[13px] text-danger">Não foi possível carregar os dados deste relatório.</p>;
  }

  // Campo zerado nos dois lados não diz nada.
  const campos = data.campos.filter((campo) => campo.relatorio !== 0 || (campo.pdf ?? 0) !== 0);
  const temPdf = campos.some((campo) => campo.pdf !== null);

  return (
    <div className="py-sm">
      <table className="w-full max-w-[640px] text-[13px]">
        <thead className="text-[11px] tracking-wide text-ink-soft uppercase">
          <tr className="border-b border-border">
            <th className="py-xs pr-md text-left font-bold">Campo</th>
            <th className="py-xs pr-md text-right font-bold">Relatório</th>
            {temPdf && <th className="py-xs text-right font-bold">PDF da folha</th>}
          </tr>
        </thead>
        <tbody>
          {campos.map((campo) => {
            const diferente = campo.pdf !== null && Math.abs(campo.pdf - campo.relatorio) >= 0.005;

            return (
              <tr key={campo.campo} className="border-b border-border/60 last:border-0">
                <td className="py-[3px] pr-md text-ink">{campo.rotulo}</td>
                <td className={`py-[3px] pr-md text-right tabular-nums ${diferente ? 'font-bold text-warning' : 'text-ink'}`}>
                  {formatar(campo.campo, campo.relatorio)}
                </td>
                {temPdf && (
                  <td className="py-[3px] text-right text-muted tabular-nums">
                    {campo.pdf === null ? '—' : formatar(campo.campo, campo.pdf)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {temPdf && (
        <p className="mt-xs text-[12px] text-muted">
          Em amarelo, o que o escritório atualizou em relação ao PDF. "—" é campo que o PDF da folha não traz.
        </p>
      )}
    </div>
  );
}
